/**
 * HistoryRecord — one row in the history list.
 *
 * Figma source: node `319:5533` (generationHistory card variant).
 *   width: FILL × HUG, cornerRadius 16, padding 8, itemSpacing 12,
 *   fill SOLID `surface/4 lvl` = rgba(255,255,255,0.16).
 *
 * Three media variants (Figma 319:5533 child frames):
 *   - img      → photo/video generation thumbnail, 42×42 rounded-home-12,
 *                clipsContent true. (Figma child `277:2029`.)
 *   - symbol   → payment / subscription credit icon, 42×42 centered
 *                emoji or `coin`/`star`-style glyph. Figma uses `💰`
 *                Raleway SemiBold 24 for the «Куплено N кредитов» row;
 *                handoff §17 also calls for `💎` on subscription credit.
 *                (Figma child `277:2323`.)
 *   - avatar   → referral profile photo, 42×42 round (rounded-full).
 *                Backend currently does not return avatar URL, so we
 *                fall back to a `user` icon inside a circular surface.
 *                (Figma child `277:2494`.)
 *
 * Meta (middle column, FILL × HUG, itemSpacing 4):
 *   - title — Raleway SemiBold 12, text/primary, single-line (HUG width
 *             with 8px gap to optional badges like «1:1», «2K» for
 *             generation rows; we keep title only for now).
 *   - date  — Raleway SemiBold 12, text/secondary, opacity 0.64,
 *             format «11 марта, 13:43».
 *
 * Balance (right column, HORIZONTAL HUG, paddingRight 8, itemSpacing 2):
 *   - amount — Raleway Bold 12, sign-prefixed (+N / -N).
 *              positive → text-success (#22C55E), negative → text-text-primary
 *              (white) per Figma 277:2057 fill.
 *   - coin icon — 12×12, color `icon/accent` (#FFAC0B) — always present
 *                 (Figma 277:2058 with variant=coin).
 *
 * NOT clickable (no onClick, no cursor-pointer per acceptance §2.4).
 *
 * Backend item shape (historyService.js):
 *   id, type: 'photo'|'video'|'payment'|'credit'|'referral',
 *   title: string, date: ISO-8601, creditsDelta: number|null,
 *   thumbnailUrl: string|null, avatarUrl?: string|null,
 *   paymentMethod?: 'card'|'crypto'|'sbp'|'stars'|'p4y'|null,
 *   paymentCurrency?: string|null
 */

import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';
import { localizedField } from '../../locales/content';
import { formatHistoryDateTime } from '../../utils/dateFormat';

const PAYMENT_METHOD_ICON = {
  card: '/icons/payment/card.svg',
  crypto: '/icons/payment/crypto.svg',
  sbp: '/icons/payment/sbp.svg',
  stars: '/icons/payment/star.svg',
  p4y: '/icons/payment/crypto.svg',
};

const PAYMENT_CURRENCY_ICON = {
  BTC: '/icons/crypto/Bitcoin.svg',
  ETH: '/icons/crypto/ethereum-eth.svg',
  TON: '/icons/crypto/ton-toncoin-logo.svg',
  USDT: '/icons/crypto/tether.svg',
};

/** Format ISO date consistently as DD/MM/YYYY, HH:mm. */
function formatDate(isoString) {
  return formatHistoryDateTime(isoString);
}

/**
 * Pick media variant from backend type.
 *   photo | video  → 'img'     (thumbnail when URL is available)
 *   payment        → 'symbol'  (subscription — diamond glyph)
 *   credit         → 'symbol'  (credit purchase — money-bag glyph)
 *   referral       → 'avatar'  (round profile photo)
 *   anything else  → 'symbol'  (safe default)
 */
function pickMediaVariant(type) {
  if (type === 'photo' || type === 'video') return 'img';
  if (type === 'referral') return 'avatar';
  return 'symbol';
}

/**
 * Pick symbol glyph for non-image, non-avatar rows.
 *   credit  → '💰' (money bag — Figma 319:5533 sample)
 *   payment → '💎' (diamond — handoff §17 «Начисление по подписке»)
 *   default → '🪙' (coin) — fallback if backend ever sends unexpected type
 */
function pickSymbolGlyph(type) {
  if (type === 'payment') return '\u{1F48E}';
  if (type === 'credit') return '\u{1F4B0}';
  return '\u{1FA99}';
}

function getPaymentDisplay(item, t) {
  const method = String(item.paymentMethod || '').toLowerCase();
  const currency = String(item.paymentCurrency || '').toUpperCase();

  if (method === 'card') {
    return {
      iconSrc: PAYMENT_METHOD_ICON.card,
      label: t('plans.paymentMethods.card'),
      alt: t('plans.paymentMethods.card'),
    };
  }

  if (method === 'sbp') {
    return {
      iconSrc: PAYMENT_METHOD_ICON.sbp,
      label: t('plans.paymentMethods.sbp'),
      alt: t('plans.paymentMethods.sbp'),
    };
  }

  if (method === 'stars') {
    return {
      iconSrc: PAYMENT_METHOD_ICON.stars,
      label: t('plans.paymentMethods.stars'),
      alt: t('plans.paymentMethods.stars'),
    };
  }

  if (method === 'p4y') {
    return {
      iconSrc: PAYMENT_METHOD_ICON.p4y,
      label: t('history.paymentMethods.p4y'),
      alt: t('history.paymentMethods.p4y'),
    };
  }

  if (method === 'crypto') {
    const normalizedCurrency =
      currency.startsWith('USDT') ? 'USDT' : currency;

    return {
      iconSrc:
        PAYMENT_CURRENCY_ICON[normalizedCurrency] ||
        PAYMENT_METHOD_ICON.crypto,
      label: normalizedCurrency || t('plans.paymentMethods.crypto'),
      alt: normalizedCurrency || t('plans.paymentMethods.crypto'),
    };
  }

  return null;
}

function MediaSlot({ type, thumbnailUrl, avatarUrl, title, paymentDisplay }) {
  const { t } = useTranslation();
  const variant = pickMediaVariant(type);

  if (variant === 'img') {
    if (thumbnailUrl) {
      return (
        <img
          src={thumbnailUrl}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-[42px] h-[42px] rounded-home-12 object-cover shrink-0"
          data-media="img"
        />
      );
    }
    return (
      <div
        data-media="img"
        className="w-[42px] h-[42px] rounded-home-12 bg-surface-2 shrink-0 flex items-center justify-center"
      >
        <Icon
          name={type === 'video' ? 'video' : 'image'}
          size={20}
          className="text-text-secondary"
        />
      </div>
    );
  }

  if (variant === 'avatar') {
    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-[42px] h-[42px] rounded-full object-cover shrink-0"
          data-media="avatar"
        />
      );
    }
    return (
      <div
        data-media="avatar"
        className="w-[42px] h-[42px] rounded-full bg-surface-2 shrink-0 flex items-center justify-center"
        aria-label={title || t('history.referral')}
      >
        <Icon name="user" size={22} className="text-text-secondary" />
      </div>
    );
  }

  if (paymentDisplay) {
    return (
      <div
        data-media="payment"
        className="w-[42px] h-[42px] rounded-home-12 bg-surface-2 shrink-0 flex items-center justify-center"
      >
        <img
          src={paymentDisplay.iconSrc}
          alt={paymentDisplay.alt}
          width={28}
          height={28}
          className="shrink-0"
        />
      </div>
    );
  }

  return (
    <div
      data-media="symbol"
      className="w-[42px] h-[42px] shrink-0 flex items-center justify-center"
    >
      <span
        aria-hidden="true"
        className="font-sans font-semibold text-[24px] leading-none"
      >
        {pickSymbolGlyph(type)}
      </span>
    </div>
  );
}

export default function HistoryRecord({ item }) {
  const { t } = useTranslation();
  const { type, date, creditsDelta, thumbnailUrl, avatarUrl } = item;
  const title = localizedField(item, 'title') || item?.title || '';
  const paymentDisplay = getPaymentDisplay(item, t);
  const metaText = paymentDisplay
    ? `${paymentDisplay.label} • ${formatDate(date)}`
    : formatDate(date);

  // Amount: «+ 100» / «- 1» Raleway Bold 12.
  // Positive → success green (text-success); negative → white (text-text-primary).
  let amountNode = null;
  if (typeof creditsDelta === 'number') {
    const sign = creditsDelta >= 0 ? '+ ' : '- ';
    const abs = Math.abs(creditsDelta);
    const colorClass =
      creditsDelta >= 0 ? 'text-text-success' : 'text-text-primary';
    amountNode = (
      <div className="flex items-center gap-0.5 shrink-0 pr-2">
        <span
          className={`font-sans font-bold text-[12px] leading-none ${colorClass}`}
        >
          {sign}
          {abs}
        </span>
        <Icon name="coin" size={12} className="text-icon-accent" />
      </div>
    );
  }

  return (
    <div
      data-testid="history-record"
      className="flex items-center gap-3 p-2 rounded-home-16 bg-glass-16"
      aria-label={title}
    >
      <MediaSlot
        type={type}
        thumbnailUrl={thumbnailUrl}
        avatarUrl={avatarUrl}
        title={title}
        paymentDisplay={paymentDisplay}
      />

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <p className="font-sans font-semibold text-[12px] leading-none text-text-primary truncate">
          {title}
        </p>
        <p className="font-sans font-semibold text-[12px] leading-none text-text-secondary opacity-[0.64]">
          {metaText}
        </p>
      </div>

      {amountNode}
    </div>
  );
}
